/**
 * Chat-runtime ("Copilot") detection — single source of truth for the
 * frontend.
 *
 * A "Copilot" is a Lambda-hosted CHAT agent (Slack/Lark-style, one message =
 * one chat turn) rather than a normal Fargate workflow. The two chat templates
 * are chat-ops-bot and zibby-copilot; forward-marked templates also carry
 * `runtime: 'chat'` on the marketplace row.
 *
 * Detection (OR):
 *   1. The marketplace row / overlay / pipeline carries `runtime === 'chat'`
 *      (forward marker, set on zibby-copilot from newer template versions).
 *   2. The marketplace slug is in CHAT_RUNTIME_SLUGS (fallback for rows that
 *      predate the runtime flag).
 *
 * Mirrors the backend services/account-copilots.js isCopilotTemplate /
 * slack-chat-mapping.js chatTemplateSlugs so the UX gate and the hard guard
 * agree on what counts as a Copilot.
 */

// Slug allow-list for chat/Lambda agents. Kept here so detection is robust even
// when the marketplace `runtime` field hasn't propagated onto a row yet.
export const CHAT_RUNTIME_SLUGS = new Set(['chat-ops-bot', 'zibby-copilot']);

/**
 * True when `pipeline` is a Copilot (chat-runtime). `pipeline` may be a deployed
 * workflow row (carries `runtime` and/or `marketplaceSlug`), a marketplace entry
 * (carries `slug` and/or `runtime`), or anything in between — we read every
 * shape these objects take.
 */
export function isChatRuntime(pipeline) {
  if (!pipeline) return false;
  const runtime = pipeline.runtime || pipeline.marketplace?.runtime;
  if (runtime === 'chat') return true;
  const slug =
    pipeline.marketplace?.slug || pipeline.marketplaceSlug || pipeline.slug;
  return !!slug && CHAT_RUNTIME_SLUGS.has(slug);
}

/** User-facing copy for the one-Copilot-per-PROJECT cap. Matches the backend
 *  deploy-gate rejection so the disabled-button hint and the 409 read
 *  identically. (The old per-ACCOUNT cap is gone: an account can run several
 *  Copilots — one per project — with a single ACCOUNT-DEFAULT among them.) */
export const COPILOT_LIMIT_MESSAGE =
  'This project already has a Copilot. One Copilot per project.';

// ── Agent-builder detection (systemManaged meta-agent) ──────────────────────
// The agent-builder is the Copilot-managed meta-agent. It is now USER-deployable
// from the marketplace but capped at ONE per ACCOUNT (backend enforces via
// BUILDER_LIMIT_REACHED; this UI just greys the button when the cap is hit).
// Mirrors the backend services/account-copilots.js isAgentBuilderTemplate /
// isDeployedAgentBuilder + config/workflow-slugs.js isAgentBuilderType.

// The canonical slug. A stray `agent-builder-2` (past resolve race) is also a
// builder — hence the startsWith below, matching isAgentBuilderType.
export const AGENT_BUILDER_SLUG = 'agent-builder';

/** True when `x` IS an agent-builder — reads a deployed workflow row
 *  (workflowType / marketplaceSlug) OR a marketplace entry (slug / the
 *  declarative systemManaged flag), so one helper covers both shapes. */
export function isAgentBuilder(x) {
  if (!x) return false;
  if (x.systemManaged === true) return true;
  const candidates = [
    x.workflowType,
    x.marketplaceSlug,
    x.marketplace && x.marketplace.slug,
    x.slug,
  ];
  return candidates.some(
    (s) => typeof s === 'string' && (s === AGENT_BUILDER_SLUG || s.startsWith(`${AGENT_BUILDER_SLUG}-`))
  );
}

/** User-facing copy for the one-agent-builder-per-ACCOUNT cap. Shown on the
 *  disabled Deploy button ONLY when the account already has a builder. Matches
 *  the backend BUILDER_LIMIT_MESSAGE + the self-host catalog copy verbatim. */
export const BUILDER_LIMIT_MESSAGE =
  'This account already has an Agent Builder. One per account.';
